import React, { ReactNode } from 'react';

import { Box, Typography } from '@material-ui/core';

import { AppSearchInput } from '../inputs';
import { SettingsCardPair, SettingsSection } from './SettingsSection';
import { useStyles } from './styles';

/** One card on a settings page; `search` is what the Search settings box matches against. */
export interface SettingsCardEntry {
  key: string;
  search: string;
  node: ReactNode;
}

export interface SettingsRow {
  /** Lays the row's cards out two-up on wide screens. */
  paired?: boolean;
  cards: SettingsCardEntry[];
}

export interface SettingsSectionConfig {
  label: string;
  description?: string;
  rows: SettingsRow[];
}

interface Props {
  title: string;
  description: string;
  sections: SettingsSectionConfig[];
  /** Pass both to show the Search settings box; leave them out on pages with only a card or two. */
  query?: string;
  onQueryChange?: (value: string) => void;
  /** Sits above the sections, full width — e.g. the pay schedule summary strip. */
  header?: ReactNode;
  /** Sits below the sections — e.g. a page-level Save button. */
  footer?: ReactNode;
}

/**
 * The settings page layout shared by Time & Attendance, Teams and Pay: a sticky
 * left column naming the page, and grouped cards on the right.
 */
export const SettingsPage = ({
  title,
  description,
  sections,
  query,
  onQueryChange,
  header,
  footer,
}: Props) => {
  const classes = useStyles();

  const trimmedQuery = (query ?? '').trim().toLowerCase();
  const visibleSections = sections
    .map(section => ({
      ...section,
      rows: section.rows
        .map(row => ({
          ...row,
          cards: row.cards.filter(card => !trimmedQuery || card.search.includes(trimmedQuery)),
        }))
        .filter(row => row.cards.length > 0),
    }))
    .filter(section => section.rows.length > 0);

  return (
    <Box className={classes.page}>
      <Box className={classes.intro}>
        <Box className={classes.introText}>
          <Typography className={classes.introTitle}>{title}</Typography>

          <Typography variant="caption" className={classes.introDescription}>
            {description}
          </Typography>
        </Box>

        {!!onQueryChange && (
          <AppSearchInput
            isSmall
            smallRounding
            isBorder
            fullWidth
            expanded
            variant="paper"
            placeholder="Search settings"
            value={query}
            onChange={e => onQueryChange(e.target.value)}
          />
        )}
      </Box>

      <Box className={classes.content}>
        {header}

        {visibleSections.map(section => (
          <SettingsSection key={section.label} label={section.label} description={section.description}>
            {section.rows.map((row, index) =>
              row.paired && row.cards.length > 1 ? (
                <SettingsCardPair key={index}>
                  {row.cards.map(card => (
                    <React.Fragment key={card.key}>{card.node}</React.Fragment>
                  ))}
                </SettingsCardPair>
              ) : (
                row.cards.map(card => <React.Fragment key={card.key}>{card.node}</React.Fragment>)
              )
            )}
          </SettingsSection>
        ))}

        {!visibleSections.length && !!trimmedQuery && (
          <Typography variant="caption" color="textSecondary">
            No settings match “{(query ?? '').trim()}”.
          </Typography>
        )}

        {footer}
      </Box>
    </Box>
  );
};
